import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import {
  Breadcrumbs,
  Button,
  SkeletonBase
} from "@unified-checkout/design-system";
import { ArrowsRepeat } from "@unified-checkout/design-system/icons/index";
import type { IButton } from "libs/design-system/src/types/Button/button";
import { formatToLocalizedDateTime } from "libs/design-system/src/utils/dateFormats";
import { ISingleIntegrationHeader } from "@app/dashboard/types/innerLayout/payment_integrations/header";
import { refreshPaypal } from "@app/dashboard/services/inner_layout/payment_integrations/refresh-paypal";
import { useLangStore } from "@app/dashboard/store/lang";
import { useSettingsUtils } from "../../../Settings/useSettingsUtils";
import styles from "./header.module.scss";

const VISIBLE_ACTION_BTNS = 2;

export const Header = ({
  data: selectedIntegration,
  isError,
  isLoading,
  refetchData
}:
  ISingleIntegrationHeader
) => {
  const id = selectedIntegration?.id;

  const { t } = useTranslation();
  const { lang } = useLangStore();
  const {internalPagesBreadcrumbs} = useSettingsUtils();

  const {
    mutate: mutateCheckPaypalOnboarding,
    isPending: isPaypalCheckLoading
  } = useMutation({
    mutationKey: [QUERY_KEYS.REFRESH_PAYPAL, id],
    mutationFn: () => refreshPaypal(),
    onSuccess: () => {
      refetchData?.();
    },
    onError: () => {
      refetchData?.();
    }
  });

  const {
    gateway_type,
    created_at,
  } = selectedIntegration || {};
  
  const isPaypal = gateway_type === "PAYPAL"
  const headerActionBtnsSharedProps = {
    size: "md",
    color: "secondary",
    variant: "outlined",
  };

  const headerActionBtns: IButton[] = [
    ... (
      isPaypal ?
        [{
          ...headerActionBtnsSharedProps,
          leadingIcon: ArrowsRepeat,
          text: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.refresh'),
          onClickHandler: () => mutateCheckPaypalOnboarding(),
          loading: isPaypalCheckLoading,
          disabled: isPaypalCheckLoading
        }] : []
    )
  ] as IButton[];

  const visibleActionBtns = headerActionBtns.slice(0, VISIBLE_ACTION_BTNS);

  return (
    !isError &&
    <div className={styles["header"]}>

      <div className={styles["header__breadcrumbs"]}>
        {
          isLoading ?
            <SkeletonBase height="1.5rem" />
            :
            <Breadcrumbs
              linksProps={internalPagesBreadcrumbs.SINGLE_PAYMENT_INTEGRATION}
            />
        }
      </div>

      {
        isLoading ?
          <SkeletonBase height="3.75rem" />
          :
          <div className={styles["header__main-info-box"]}>

            <div className={styles["header__main-info-box__info"]}>

              <div className={styles["header__main-info-box__info__amount-status-box"]}>

                <span>
                  {t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.integration').replace('{{id}}', String(id ?? ''))}
                </span>

              </div>
              <div className={styles["header__main-info-box__info__date"]}>
                {`${t('innerLayout.transactionsPage.singleTransactionsPage.created')}
                ${created_at ? formatToLocalizedDateTime(created_at, lang) : ""}`}
              </div>
            </div>

            <div className={styles["header__main-info-box__actions"]}>
              {
                visibleActionBtns?.map((btn, index) => {
                  return <Button
                    key={`single-transaction-header-action-btn-${index}`}
                    {...btn}
                  />
                })
              }

            </div>
          </div>
      }
    </div>
  )
};